import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import CozyTabs from '@quintoandar/cozy-core/Tabs';
import CozyTab from '@quintoandar/cozy-core/Tab';
import Box from '@quintoandar/cozy-core/Box';

import AppBar from 'components/AppBar';
import { isProblemSessionRescheduleEnabled } from 'data/feature-flags/problemSessionReschedule';
import ROUTES, { routesNames } from 'routes/constants';

import Tabs from './Tabs';
import messages from './messages';
import { makeSelectHasToReschedulePhotoSessions } from './selectors';

const TABS = Object.freeze({
  NOT_STARTED: 'NOT_STARTED',
  RESCHEDULE: 'RESCHEDULE',
  STARTED: 'STARTED',
});

const TAB_ROUTES = Object.freeze({
  [TABS.NOT_STARTED]: ROUTES.NOT_STARTED_PHOTO_SESSIONS,
  [TABS.RESCHEDULE]: ROUTES.RESCHEDULE_PHOTO_SESSIONS,
  [TABS.STARTED]: ROUTES.STARTED_PHOTO_SESSIONS,
});

const TAB_ROUTE_NAMES = Object.freeze({
  [TABS.NOT_STARTED]: routesNames.notStartedPhotoSessions,
  [TABS.RESCHEDULE]: routesNames.reschedulePhotoSessions,
  [TABS.STARTED]: routesNames.startedPhotoSessions,
});

const getActiveTab = (routeName, showRescheduleTab) => {
  if (showRescheduleTab && routeName === routesNames.reschedulePhotoSessions) {
    return TABS.RESCHEDULE;
  }
  if (routeName === routesNames.startedPhotoSessions) {
    return TABS.STARTED;
  }
  return TABS.NOT_STARTED;
};

export const PhotoSessions = ({
  intl,
  route,
  onTabChange,
  onUnavailableRescheduleRoute,
  hasToReschedulePhotoSessions,
}) => {
  const [showRescheduleTab, setShowRescheduleTab] = useState(false);
  const activeTab = getActiveTab(route.name, showRescheduleTab);

  useEffect(() => {
    let active = true;
    isProblemSessionRescheduleEnabled().then((enabled) => {
      if (!active) {
        return;
      }

      setShowRescheduleTab(enabled);

      // Keep URL aligned when a deep link hits /reagendar without the flag.
      if (!enabled && route.name === routesNames.reschedulePhotoSessions) {
        onUnavailableRescheduleRoute();
      }
    });
    return () => {
      active = false;
    };
  }, [route.name, onUnavailableRescheduleRoute]);

  const onChange = useCallback((tab) => {
    onTabChange(tab, route);
  }, [onTabChange, route]);

  const tabs = (
    <Box px={1}>
      <CozyTabs activeTab={activeTab} onChange={onChange}>
        <CozyTab
          id={TABS.NOT_STARTED}
          label={intl.formatMessage(messages.notStartedPhotoSessions)}
        />
        {showRescheduleTab && (
          <CozyTab
            id={TABS.RESCHEDULE}
            label={intl.formatMessage(messages.reschedulePhotoSessions)}
            staticBadge={hasToReschedulePhotoSessions}
            badgeVariant="critical"
          />
        )}
        <CozyTab
          id={TABS.STARTED}
          label={intl.formatMessage(messages.startedPhotoSessions)}
        />
      </CozyTabs>
    </Box>
  );

  return (
    <>
      <AppBar
        title={messages.title}
        tabs={tabs}
      />
      <Tabs route={route} showRescheduleTab={showRescheduleTab} />
    </>
  );
};

PhotoSessions.propTypes = {
  intl: PropTypes.object,
  route: PropTypes.shape({
    name: PropTypes.string,
  }),
  onTabChange: PropTypes.func.isRequired,
  onUnavailableRescheduleRoute: PropTypes.func.isRequired,
  hasToReschedulePhotoSessions: PropTypes.bool,
};

PhotoSessions.defaultProps = {
  hasToReschedulePhotoSessions: false,
};

const mapStateToProps = createStructuredSelector({
  hasToReschedulePhotoSessions: makeSelectHasToReschedulePhotoSessions(),
});

export const mapDispatchToProps = (dispatch) => ({
  onTabChange: (tab, route) => {
    if (TAB_ROUTE_NAMES[tab] !== route.name) {
      dispatch(push(TAB_ROUTES[tab]));
      window.scrollTo(0, 0);
    }
  },
  onUnavailableRescheduleRoute: () => {
    dispatch(push(ROUTES.NOT_STARTED_PHOTO_SESSIONS));
  },
});

export default compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps)
)(PhotoSessions);
